import React from 'react';
import { useIntl } from 'react-intl';

import Accordion from 'carbon-react/lib/components/accordion/accordion.component';

import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';

import { DataBuilderFormContainer } from '../../../../ui/data-builder/data-builder-form-container/data-builder-form-container';
import { FormulaContentLexical } from '../../../../ui/data-builder/formula-content/formula-content-lexical';
import { FormulaDriverHelp } from '../../../components/formula-driver-help/formula-driver-help';
import { FormulaErrors } from '../../../../ui/data-builder/formula-content-form/formula-errors/formula-errors';
import { useDriverBuilderContext } from '../../../contexts/driver-builder-context/driver-builder-context';
import { useProcessedFormulaOptions } from '../../../hooks/useProcessedFormulaOptions/useProcessedFormulaOptions';

interface FormulaDriverWizardScreenProps {
  onClose: () => void;
  onCreate: () => void;
  onUpdate: () => void;
}

export const FormulaDriverWizardScreen = ({ onClose, onCreate, onUpdate }: FormulaDriverWizardScreenProps) => {
  const intl = useIntl();

  const { formulaForm, sharedForm, view } = useDriverBuilderContext();
  const {
    addToGroup,
    decimalDisplay,
    errors,
    handleAddToGroupChange,
    handleDecimalDisplayChange,
    handleInputMethodChange,
    handleNameChange,
    inputMethod,
    name,
    revalidate
  } = sharedForm;
  const {
    formulaError,
    handleFormulaChange,
    handleOutputFrequencyChange,
    handleOutputOnDayChange,
    handleOutputOnMonthChange,
    outputFrequency,
    outputOnDay,
    outputOnMonth,
    restoreFormulaStringRef
  } = formulaForm;

  const { formulaDriver, formulaOptions, groups, errorCreatingDriver, errorUpdatingDriver } = view.state;

  const processedFormulaOptions = useProcessedFormulaOptions(formulaOptions?.non_financial_drivers_and_groups);

  return (
    <WizardScreen
      leftSideButtons={
        <FormulaErrors
          formulaContent={restoreFormulaStringRef.current}
          formulaErrorCode={formulaError?.error_code}
          show={!!formulaError && !formulaError.is_valid}
        />
      }
      onCancel={onClose}
      onCreate={onCreate}
      onUpdate={onUpdate}
      footerMode={formulaDriver ? FooterMode.UPDATE : FooterMode.CREATE}
    >
      <DataBuilderFormContainer
        addToGroup={addToGroup}
        decimalDisplay={decimalDisplay}
        errors={errors}
        groups={groups}
        inputMethod={inputMethod}
        isAddingNew={!formulaDriver}
        isSaving={!!(errorCreatingDriver || errorUpdatingDriver)}
        name={name}
        onAddToGroupChange={handleAddToGroupChange}
        onDecimalDisplayChange={handleDecimalDisplayChange}
        onFieldBlur={revalidate}
        onInputMethodChange={handleInputMethodChange}
        onNameChange={handleNameChange}
        saveError={!!(errorCreatingDriver || errorUpdatingDriver)}
      >
        <FormulaContentLexical
          errors={errors}
          formulaOptions={processedFormulaOptions}
          formulaString={restoreFormulaStringRef.current}
          onFormulaChange={handleFormulaChange}
          onOutputFrequencyChange={handleOutputFrequencyChange}
          onOutputOnDayChange={handleOutputOnDayChange}
          onOutputOnMonthChange={handleOutputOnMonthChange}
          outputFrequency={outputFrequency}
          outputOnDay={outputOnDay}
          outputOnMonth={outputOnMonth}
          showFieldErrors={!!errors}
        />
      </DataBuilderFormContainer>
      <Accordion
        variant="subtle"
        borders="none"
        mt={4}
        width="720px"
        title={intl.formatMessage({
          id: 'predict.non_financial_drivers.help_creating_a_formula',
          defaultMessage: 'Help creating a formula'
        })}
      >
        <FormulaDriverHelp />
      </Accordion>
    </WizardScreen>
  );
};
